import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone, Mail, CheckCircle, MessageCircle, ArrowRight, Award, Car,
  PoundSterling, MapPin, Sparkles, Target, Clock, Users, Star, Play, ChevronRight,
} from 'lucide-react';
import SEOFooter from '@/components/marketing/SEOFooter';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import ScrollingReviewBar from '@/components/marketing/ScrollingReviewBar';
import { Button } from '@/components/ui/button';
import IntensiveCourseEnquiry from '@/components/marketing/IntensiveCourseEnquiry';
import { courseEnquiryAttribution } from '@/lib/courseEnquiryAttribution';

import {
  generateEnhancedLocalBusinessSchema,
  generateBreadcrumbSchema,
} from '@/components/marketing/SEOHead';
import studentPass1 from '@/assets/student-pass-1.jpg';
import studentPass2 from '@/assets/student-pass-2.jpg';
import studentPass3 from '@/assets/student-pass-3.jpg';

import ianVideoAsset from '@/assets/ian-intensive-video.mp4.asset.json';
import ianPosterAsset from '@/assets/ian-intensive-poster.jpg.asset.json';
import { PHONE_SMS, PHONE_TEL } from '@/config/business';

const ianVideo = ianVideoAsset.url;
const ianPoster = ianPosterAsset.url;

const wa = (msg: string) =>
  `${PHONE_SMS}?&body=${encodeURIComponent(msg)}`;

const WHATSAPP_URL = wa("Hi Ian, I'd like to check availability for an intensive driving course in Bath.");

const heroTrust = [
  { icon: Award, label: 'DVSA Grade A Instructor' },
  { icon: Clock, label: '25+ Years Experience' },
  { icon: Users, label: 'Ian Teaches Every Course' },
  { icon: Car, label: 'Manual & Automatic Lessons' },
];



const areas = [
  { name: 'Bath', note: 'Home base · door-to-door pickup across the city.' },
  { name: 'Chippenham', note: 'DVSA test centre · rural & dual-carriageway routes.' },
  { name: 'Kingswood', note: 'DVSA test centre · Bristol-side, roundabout-heavy.' },
];

const testimonials = [
  {
    image: studentPass1,
    name: 'Andy',
    firstTime: true,
    quote: 'Ian kept me calm under pressure. Walked out with my pass first time.',
    centre: 'Chippenham',
    when: 'Mar 2026',
  },
  {
    image: studentPass2,
    name: 'Masha',
    firstTime: true,
    quote: 'Intensive course was structured and clear. Confident from day one.',
    centre: 'Kingswood',
    when: 'Feb 2026',
  },
  {
    image: studentPass3,
    name: 'Marcella',
    firstTime: false,
    quote: 'Honest feedback throughout. I knew exactly when I was ready.',
    centre: 'Kingswood',
    when: 'Jan 2026',
  },
];

const pricingFaqs = [
  {
    q: 'Do the course prices include everything?',
    a: 'Yes — every intensive course includes a full mock test, pre-drive warm-up on test day and door-to-door pickup. No hidden extras.',
  },
  {
    q: 'What is the cancellation policy?',
    a: 'Sessions cancelled with at least 24 hours’ notice are rescheduled free of charge. Short-notice cancellations may be chargeable.',
  },
  {
    q: 'Manual or automatic?',
    a: 'Every intensive course is available in manual or automatic — choose your transmission on the pricing toggle above.',
  },
];

const faqs = [
  { q: 'How much are intensive driving courses in Bath?', a: 'Intensive courses start at £695 for a 5-hour last-minute block, up to £3,995 for a full 35-hour manual programme (automatic from £695 to £4,495). My 10-hour course costs £1,395 manual or £1,495 automatic. Every course includes a full mock test and pre-drive warm-up.' },
  { q: 'Do you offer intensive driving courses in Bath?', a: 'Yes. I offer 5, 10, 15, 25 and 35-hour intensive blocks built around your test date, with focused coaching from me personally.' },
  { q: 'Who is the 10-hour intensive course for?', a: 'My 10-hour course is for learners with some prior driving experience seeking focused test preparation. It costs £1,395 manual or £1,495 automatic and includes a full mock test and pre-drive warm-up.' },
  { q: 'Do you cover Chippenham and Kingswood?', a: 'Yes. Courses are Bath-based with door-to-door pickup, and test prep covers both Chippenham and Kingswood DVSA test centres — Bath itself has no test centre.' },
  { q: 'Are courses manual or automatic?', a: 'Both. Every intensive course is available in manual or automatic. Passing in a manual means you can drive any car on the road.' },
  { q: 'Is there a charge or commitment to enquire?', a: 'No. Checking availability is free, with no commitment or upfront payment to enquire. Choose a course and send the short form, or text me directly. If you decide to book, the booking and payment terms apply.' },
];

const localBusinessSchema = generateEnhancedLocalBusinessSchema();
const breadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://best-drive.co.uk' },
  { name: 'Intensive Driving Courses Bath', url: 'https://best-drive.co.uk' },
]);
const combinedFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [...faqs, ...pricingFaqs].map((faq: any) => ({
    '@type': 'Question',
    name: faq.q || faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.a || faq.answer },
  })),
};
const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://best-drive.co.uk/#website',
  url: 'https://best-drive.co.uk',
  name: 'Best-Drive Bath',
  publisher: { '@id': 'https://best-drive.co.uk/#localbusiness' },
};

// Course schema for each intensive block (SEO: rich results eligibility)
export const courseListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Intensive Driving Courses Bath',
  itemListElement: [
    { hours: 5, manual: 695, auto: 695, desc: 'Last-minute 5-hour intensive with mock test and pre-drive warm-up before your test.' },
    { hours: 10, manual: 1395, auto: 1495, desc: '10-hour intensive course for learners with some prior driving experience seeking focused test preparation, with a full mock test and pre-drive warm-up.' },
    { hours: 15, manual: 2295, auto: 2495, desc: '15-hour intensive course for returning or partly-trained learners in Bath.' },
    { hours: 25, manual: 3195, auto: 3495, desc: '25-hour intensive course for improvers building a confident, test-ready drive.' },
    { hours: 35, manual: 3995, auto: 4495, desc: '35-hour full intensive programme for beginners across Bath, Bristol and Chippenham.' },
  ].map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Course',
      name: `${c.hours}-Hour Intensive Driving Course Bath`,
      description: c.desc,
      provider: { '@type': 'DrivingSchool', '@id': 'https://best-drive.co.uk/#localbusiness', name: 'Best-Drive Bath' },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'onsite',
        instructor: { '@type': 'Person', name: 'Ian Perry', jobTitle: 'DVSA Grade A Driving Instructor' },
      },
      timeRequired: `PT${c.hours}H`,
      offers: [
        { '@type': 'Offer', price: c.manual, priceCurrency: 'GBP', category: 'Manual' },
        { '@type': 'Offer', price: c.auto, priceCurrency: 'GBP', category: 'Automatic' },
      ],
    },
  })),
};

// Reusable primary CTA class — matches the hero title gradient
const primaryCta =
  'inline-flex min-h-12 items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-[15px] bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 text-[#06121a] shadow-[0_10px_30px_-10px_rgba(45,212,191,0.6)] hover:-translate-y-0.5 transition-all';

type IntensiveTransmission = 'manual' | 'automatic';

export const intensiveBlocks: Array<{
  hours: number;
  prices: Record<IntensiveTransmission, string>;
  bestFor: string;
  bullets: string[];
  badge?: string;
  highlight?: boolean;
}> = [
  {
    hours: 5,
    prices: { manual: '£695', automatic: '£695' },
    bestFor: 'Test already booked — last-minute focused prep',
    bullets: ['Full mock test included', 'Pre-drive warm-up before your test', 'Built around your test date'],
    badge: 'Last-Minute',
  },
  {
    hours: 10,
    prices: { manual: '£1,395', automatic: '£1,495' },
    bestFor: 'Some prior driving experience — focused test preparation',
    bullets: ['Full mock test included', 'Pre-drive warm-up before your test', '2-hour structured sessions'],
    badge: 'Most Popular',
    highlight: true,
  },
  {
    hours: 15,
    prices: { manual: '£2,295', automatic: '£2,495' },
    bestFor: 'Returning or partly-trained learners',
    bullets: ['Full mock test included', 'Pre-drive warm-up before your test', '2-hour structured sessions', 'Completed in 3–4 weeks'],
  },
  {
    hours: 25,
    prices: { manual: '£3,195', automatic: '£3,495' },
    bestFor: 'Improvers building a confident, test-ready drive',
    bullets: ['Full mock test included', 'Pre-drive warm-up before your test', '2-hour structured sessions', 'Recommended for improvers'],
  },
  {
    hours: 35,
    prices: { manual: '£3,995', automatic: '£4,495' },
    bestFor: 'Beginners wanting a full structured programme',
    bullets: ['Full mock test included', 'Pre-drive warm-up before your test', '2-hour structured sessions', 'Structured over 5–7 weeks'],
    badge: 'Premium',
  },
];

const IntensiveCoursePricing: React.FC<{ onEnquire: (hours: number, transmission: IntensiveTransmission) => void; enquiryForm: React.ReactNode }> = ({ onEnquire, enquiryForm }) => {
  const [transmission, setTransmission] = React.useState<IntensiveTransmission>('manual');
  return (
    <section id="intensive-courses" className="py-20 sm:py-24 px-5 scroll-mt-20 bg-gradient-to-b from-[#070b16] via-[#0a1226] to-[#070b16]">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">Intensive Courses</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight mb-4">
            Intensive driving courses —{' '}
            <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
              manual or automatic
            </span>
          </h2>
          <p className="max-w-xl mx-auto text-base text-white/80 leading-relaxed">
            Focused blocks built around your test date. Choose your transmission and pick the size that suits you.
          </p>
        </div>

        {/* Manual / Automatic toggle */}
        <div className="flex justify-center mb-10">
          <div role="tablist" aria-label="Transmission" className="inline-flex p-1 rounded-full border border-white/10 bg-white/[0.05] backdrop-blur">
            {(['manual', 'automatic'] as const).map((t) => {
              const active = transmission === t;
              return (
                <button
                  key={t}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTransmission(t)}
                  className={`px-6 py-2.5 rounded-full text-base font-semibold capitalize transition-all ${
                    active
                      ? 'bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 text-[#06121a] shadow-[0_8px_24px_-8px_rgba(45,212,191,0.6)]'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-5 gap-x-5 gap-y-7 xl:gap-x-3">
          {intensiveBlocks.map((b) => (
            <div
              key={b.hours}
              className={`relative min-w-0 sm:col-span-2 sm:last:col-start-2 lg:[&:nth-child(4)]:col-start-2 lg:last:col-start-4 xl:col-span-1 xl:[&:nth-child(4)]:col-start-auto xl:last:col-start-auto rounded-3xl border p-7 xl:p-5 flex flex-col bg-white/[0.04] backdrop-blur ${
                b.highlight ? 'border-teal-300/40 shadow-[0_20px_60px_-20px_rgba(20,184,166,0.4)]' : 'border-white/10'
              }`}
            >
              {b.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase bg-gradient-to-r from-emerald-400 to-teal-500 text-[#06121a] whitespace-nowrap">
                  {b.badge}
                </span>
              )}
              <p className="text-2xl sm:text-3xl xl:text-2xl font-bold tracking-tight mb-2" style={{ color: '#ec4899' }}>{b.hours} Hours</p>
              <p className="text-3xl xl:text-2xl font-semibold text-white mb-2 leading-none">{b.prices[transmission]}</p>
              <p className="text-sm text-white/60 capitalize mb-5">{transmission} · one course price</p>
              <p className="text-base xl:text-sm text-teal-200/90 mb-5 leading-relaxed">{b.bestFor}</p>
              <ul className="space-y-3 mb-6">
                {b.bullets.map((li) => (
                  <li key={li} className="flex items-start gap-2.5 xl:gap-2 text-base xl:text-sm text-white/85 leading-relaxed">
                    <CheckCircle className="h-5 w-5 text-teal-300 mt-0.5 shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                aria-controls="course-enquiry"
                onClick={() => onEnquire(b.hours, transmission)}
                className="mt-auto btn-premium px-5 rounded-full text-base"
              >
                Enquire <ArrowRight className="h-4 w-4" />

              </Button>
            </div>
          ))}
        </div>
        {enquiryForm}
      </div>
    </section>
  );
};


const BestDriveBath = ({ courseLocation }: { courseLocation?: 'Cheltenham' } = {}) => {
  const isCheltenham = courseLocation === 'Cheltenham';
  const locationName = isCheltenham ? 'Cheltenham' : 'Bath';
  const location = useLocation();
  const [attribution] = React.useState(() => isCheltenham
    ? courseEnquiryAttribution(location.pathname, location.search, document.referrer) : '');
  const pageFaqs = isCheltenham ? faqs.map((faq, index) => {
    if (index === 1) return { q: 'Can I enquire about an intensive course in Cheltenham?', a: 'Yes. Tell me your experience, preferred timing and pickup needs. I personally teach every course and will confirm whether I can accommodate your location and dates before you book.' };
    if (index === 3) return { q: 'Where will my Cheltenham course and test preparation take place?', a: 'We agree the meeting point, lesson area and preparation for your booked test before you commit. Best-Drive is based in Bath; this page does not promise a Cheltenham office, test slot or guaranteed pickup.' };
    return { q: faq.q.replace(/Bath/g, 'Cheltenham'), a: faq.a };
  }) : faqs;
  const pageAreas = isCheltenham ? [
    { name: 'Cheltenham enquiries', note: 'Discuss your location, experience and preferred course dates with Ian.' },
    { name: 'Meeting point', note: 'Agree pickup or a meeting point with Ian before committing to a course.' },
    { name: 'Your driving test', note: 'Book your own DVSA test; coaching and test support are agreed around your plans.' },
  ] : areas;
  const [videoPlaying, setVideoPlaying] = React.useState(false);
  const [enquiry, setEnquiry] = React.useState<{ hours?: number; transmission?: IntensiveTransmission; origin: 'hero' | 'card' } | null>(null);
  const openEnquiry = (hours?: number, transmission?: IntensiveTransmission) => {
    setEnquiry({ hours, transmission, origin: hours ? 'card' : 'hero' });
    requestAnimationFrame(() => {
      const form = document.getElementById('course-enquiry');
      form?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      form?.querySelector<HTMLElement>(hours ? '#ice-name' : '#ice-course')?.focus({ preventScroll: true });
    });
  };
  const enquiryForm = enquiry ? <IntensiveCourseEnquiry hours={enquiry.hours} transmission={enquiry.transmission} attribution={attribution || undefined} /> : null;
  const path = location.pathname.replace(/\/+$/, '') || '/';
  // The homepage and /bath both show the Bath driving-lessons landing content.
  // Each keeps its own self-referencing canonical so they don't compete.
  const isBathLanding = path === '/bath' || path === '/';
  const pageTitle = isCheltenham ? 'Intensive Driving Courses Cheltenham | Ian Perry | Best-Drive' : isBathLanding
    ? 'Driving Lessons Bath | DVSA Grade A Instructor'
    : 'Intensive Driving Courses in Bath | Ian Perry, DVSA Grade A';
  const pageDescription = isCheltenham ? 'Cheltenham intensive driving course enquiries with Ian Perry: DVSA Grade A, 25+ years and instructor trainer. Personal manual or automatic tuition. Enquire free.' : isBathLanding
    ? 'Driving lessons in Bath with Ian Perry, DVSA Grade A instructor. Manual & automatic lessons and intensive courses across Bath, Chippenham and Kingswood.'
    : 'Intensive driving courses in Bath, taught personally by Ian Perry, DVSA Grade A instructor with 25+ years’ experience. Manual or automatic. Enquire free.';
  const canonicalUrl = path === '/'
    ? 'https://best-drive.co.uk/'
    : `https://best-drive.co.uk${path}`;



  // Named anchors surfaced to Google as jump links / potential sitelinks
  const pageAnchors = [
    { id: 'intensive-courses', label: 'Intensive course prices — manual or automatic' },
    { id: 'meet-ian', label: 'Meet Ian' },
    { id: 'pricing-faq', label: 'Pricing questions' },
  ];
  const siteNavigationSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'On this page',
    itemListElement: pageAnchors.map((a, i) => ({
      '@type': 'SiteNavigationElement',
      position: i + 1,
      name: a.label,
      url: `${canonicalUrl.replace(/\/$/, '')}/#${a.id}`,
    })),
  };
  return (

    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={isCheltenham ? 'intensive driving courses Cheltenham, automatic intensive Cheltenham, manual intensive Cheltenham, Ian Perry DVSA Grade A instructor' : 'intensive driving lessons Bath, intensive driving course Bath, intensive driving courses Bristol, crash driving course Bath, automatic intensive Bath, manual intensive Bath, DVSA Grade A instructor Bath, Ian Perry driving instructor, driving lessons Chippenham, driving lessons Kingswood'} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        {!isCheltenham && <meta name="geo.region" content="GB-SOM" />}
        <meta name="geo.placename" content={locationName} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en-gb" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://best-drive.co.uk/og-image.jpg?v=2" />
        <meta property="og:image:width" content="1500" />
        <meta property="og:image:height" content="1000" />
        <meta property="og:image:alt" content="Best Drive Driving School Mercedes in Bath" />
        <meta property="og:site_name" content="Best-Drive Bath" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://best-drive.co.uk/og-image.jpg?v=2" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(isCheltenham ? generateBreadcrumbSchema([{ name: 'Home', url: 'https://best-drive.co.uk/' }, { name: 'Intensive Driving Courses Cheltenham', url: canonicalUrl }]) : breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(isCheltenham ? { ...combinedFaqSchema, mainEntity: [...pageFaqs, ...pricingFaqs].map(faq => ({ '@type': 'Question', name: faq.q, acceptedAnswer: { '@type': 'Answer', text: faq.a } })) } : combinedFaqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(isCheltenham ? { ...courseListSchema, name: 'Intensive Driving Courses Cheltenham', itemListElement: courseListSchema.itemListElement.map(entry => ({ ...entry, item: { ...entry.item, name: entry.item.name.replace('Bath', 'Cheltenham'), description: `${entry.item.timeRequired.replace('PT', '').replace('H', '')}-hour intensive driving course, taught personally by Ian Perry. Location and dates subject to agreement.`, url: canonicalUrl } })) } : courseListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(siteNavigationSchema)}</script>

      </Helmet>


      <div className={isCheltenham ? 'marketing-theme min-h-screen bg-background text-foreground' : 'min-h-screen bg-[#070b16] text-white'}>
        <MarketingHeader />
        <ScrollingReviewBar />

        <main className="pb-20">
        {/* ===== 1. HERO ===== */}
        <section className="relative overflow-hidden bg-gradient-to-b from-[#070b16] via-[#0a1124] to-[#070b16]">
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'radial-gradient(circle at 20% 15%, rgba(20,184,166,0.20), transparent 55%), radial-gradient(circle at 80% 90%, rgba(56,189,248,0.12), transparent 60%)',
            }}
          />
          <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-14 pb-14 sm:pt-20 sm:pb-20 text-center animate-fade-in">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase mb-7 bg-amber-400/10 border border-amber-300/30 text-amber-200">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
              {isCheltenham ? 'Personal intensive tuition — Cheltenham enquiries' : 'Personal intensive tuition — Bath, Chippenham, Kingswood'}
            </span>

            <h1 className="text-[34px] leading-[1.1] sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {isCheltenham ? 'Intensive Driving Courses Cheltenham' : <>{isBathLanding ? 'Driving Lessons in Bath with a' : 'Intensive Driving Courses in Bath —'}{' '}
              <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
                {isBathLanding ? 'DVSA Grade A Instructor' : 'Taught Personally by Ian Perry'}
              </span></>}
            </h1>

            <p className="text-base sm:text-lg text-white/90 mb-8 max-w-xl mx-auto leading-relaxed">
              I’m Ian Perry, a DVSA Grade A instructor with 25+ years’ experience{isCheltenham ? ' and an instructor trainer' : ''}. I personally teach every course, manual or automatic — so you know who you’ll learn with before you book.
            </p>
            {isCheltenham && <p className="text-base text-foreground mb-6 max-w-xl mx-auto leading-relaxed">
              Looking for a focused course from Cheltenham? Whether you’re in the town centre, Pittville or Charlton Kings, tell me your starting point and preferred dates. I’ll confirm lesson locations and travel arrangements with you before you book.
            </p>}

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              <Button type="button" onClick={() => openEnquiry()} aria-controls="course-enquiry" aria-expanded={enquiry?.origin === 'hero'} className="btn-premium min-h-12 h-auto px-6 py-3.5 rounded-full text-[15px]">
                Check availability with Ian <ArrowRight className="h-4 w-4" />
              </Button>
              <a
                href={isCheltenham ? wa("Hi Ian, I'd like to check availability for an intensive driving course in Cheltenham.") : WHATSAPP_URL}
                aria-label="Text Ian for a personal reply about intensive courses"
                className="inline-flex min-h-12 items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-[15px] border border-border text-foreground hover:bg-accent transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Text Ian
              </a>
            </div>
            <p className="text-[12px] text-white/60 mb-6">Enquiring is free — no commitment and no upfront payment to enquire. Booking terms apply if you decide to book.</p>


            {/* Trust strip */}
            <p className="text-[13px] text-white/75 mb-9">
              <span className="inline-flex items-center gap-1">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> 5.0 Google rating
              </span>
              <span className="mx-2 text-white/30">·</span>
              <span>DVSA Grade A instructor</span>
              <span className="mx-2 text-white/30">·</span>
              <span>{isCheltenham ? 'Cheltenham enquiries · dates agreed with Ian' : '📍 Bath · Chippenham · Kingswood'}</span>
            </p>

            {/* Horizontal scroll-snap credential chips */}
            <ul className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-5 px-5 sm:justify-center sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {heroTrust.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="snap-start shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 bg-white/[0.05]"
                >
                  <Icon className="h-4 w-4 text-teal-300" />
                  <span className="text-[13px] font-medium text-white/90 whitespace-nowrap">{label}</span>
                </li>
              ))}
            </ul>

            {/* Featured course — no unsubstantiated outcome claim */}
            <div className="mt-9 mx-auto max-w-xl">
              <div className="relative overflow-hidden rounded-3xl border border-teal-300/30 bg-gradient-to-br from-teal-400/[0.14] via-emerald-400/[0.08] to-transparent p-6 sm:p-7 text-center shadow-[0_20px_60px_-25px_rgba(45,212,191,0.45)]">
                <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-teal-400/25 blur-3xl" aria-hidden="true" />
                <div className="relative">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-teal-300/30 bg-teal-400/10 text-[10.5px] font-semibold tracking-[0.2em] uppercase text-teal-200">
                    <Star className="h-3 w-3 fill-teal-300 text-teal-300" /> Most Popular course
                  </span>
                  <p className="mt-4 text-6xl sm:text-7xl font-bold leading-none tracking-tight bg-gradient-to-r from-teal-200 via-emerald-200 to-teal-300 bg-clip-text text-transparent">
                    10 hours
                  </p>
                  <p className="mt-3 text-base sm:text-lg font-semibold text-white">
                    Focused preparation with me personally
                  </p>
                  <p className="mt-1.5 text-[13px] text-white/75 leading-relaxed">
                    Some driving experience already? My 10-hour course focuses on the skills you need to practise. £1,395 manual · £1,495 automatic.
                  </p>
                  <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
                    I’ll help you assess whether this block suits your needs — no course guarantees a test pass.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {enquiry?.origin === 'hero' && <section className="px-5 pb-12" aria-label="Intensive course enquiry">{enquiryForm}</section>}

        {/* ===== 2. PROOF STRIP ===== */}
        <section aria-label="Local service summary" className="border-y border-white/[0.06] bg-white/[0.02]">
          <div className="max-w-5xl mx-auto px-5 py-4 text-center text-[13px] sm:text-sm text-white/80">
            {isCheltenham ? 'Cheltenham course enquiries · Meeting point and dates agreed before booking · Manual & automatic' : 'Bath-based intensive courses · Door-to-door across Bath · Manual & automatic options'}
          </div>
        </section>

        {/* ===== 2b. ON-PAGE ANCHOR NAV (jump links for Google) ===== */}
        <nav aria-label="On this page" className="border-b border-white/[0.06] bg-white/[0.02]">
          <ul className="max-w-5xl mx-auto px-5 py-3 flex gap-2.5 overflow-x-auto justify-start sm:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {pageAnchors.map((a) => (
              <li key={a.id} className="shrink-0">
                <a
                  href={`#${a.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/10 bg-white/[0.05] text-[13px] font-medium text-white/85 hover:text-white hover:bg-white/[0.09] transition-colors whitespace-nowrap"
                >
                  {a.label}
                  <ChevronRight className="h-3.5 w-3.5 text-teal-300" />
                </a>
              </li>
            ))}
          </ul>
        </nav>


        {/* ===== 3. MEET IAN ===== */}
        <section id="meet-ian" aria-labelledby="meet-ian-heading" className="relative py-20 sm:py-24 px-5 scroll-mt-20">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 50% at 80% 20%, rgba(45,212,191,0.08), transparent 60%)' }} />
          <div className="container mx-auto max-w-6xl relative">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">
                  Meet your instructor
                </p>
                <h2 id="meet-ian-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight text-white mb-6">
                  Meet Ian
                </h2>
                <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-5">
                  I’m Ian, the instructor behind BEST-DRIVE. With a larger school, your instructor may be allocated after you enquire. Here, you speak to the person who will teach every session: me. I get to know your driving and build on it, with consistent feedback and a plan tailored to you.
                </p>

                {/* Quick-fact pills */}
                <div className="flex flex-wrap gap-2 mt-5 mb-6">
                  {(isCheltenham ? ['Manual lessons', 'Automatic lessons', 'Ian teaches every lesson', 'Instructor trainer'] : ['Manual lessons', 'Automatic lessons', 'Door-to-door', 'Bath-based']).map((f) => (

                    <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium bg-teal-400/10 border border-teal-300/25 text-teal-100">
                      <CheckCircle className="h-3 w-3 text-teal-300" /> {f}
                    </span>
                  ))}
                </div>

                <div className="mt-7">
                  <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-3">Qualifications</p>
                  <ul className="space-y-3">
                    {[
                      'DVSA Grade A instructor',
                      'ASDAN qualified',
                      'City & Guilds teaching qualification',
                      'Trained over 100 people to become driving instructors',
                      'Delivered road safety courses for local councils',
                      'Intensive courses tailored to your pace',
                      'Manual and automatic options available',
                      isCheltenham ? 'Lesson location and test preparation agreed personally' : 'Support for Bath, Chippenham, and Kingswood test routes',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-base text-white/90">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-400/15 border border-teal-300/30">
                          <CheckCircle className="h-3 w-3 text-teal-300" />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              <div className="order-1 lg:order-2 relative">
                <div aria-hidden="true" className="absolute -inset-2 rounded-[28px] opacity-50 blur-2xl"
                  style={{ background: 'linear-gradient(135deg, rgba(45,212,191,0.22), rgba(56,189,248,0.16))' }} />
                <div className="relative group rounded-2xl overflow-hidden border border-white/10 bg-[#06101c] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] max-w-xl mx-auto">
                  <div className="aspect-video relative">
                    <video
                      className="h-full w-full object-cover"
                      controls
                      preload="metadata"
                      playsInline
                      poster={ianPoster}
                      aria-label="Meet Ian Perry — DVSA Grade A driving instructor in Bath"
                      onPlay={() => setVideoPlaying(true)}
                      onPause={() => setVideoPlaying(false)}
                      onEnded={() => setVideoPlaying(false)}
                    >
                      <source src={ianVideo} type="video/mp4" />
                      <track kind="captions" srcLang="en" label="English" />
                      Your browser does not support embedded video.
                    </video>
                    {/* Gradient overlay + play hint — hidden once the video plays */}
                    {!videoPlaying && (
                      <>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-90">
                          <span className="h-16 w-16 rounded-full bg-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.4)] flex items-center justify-center">
                            <Play className="h-7 w-7 text-[#06121a] ml-1" fill="#06121a" />
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-center text-[13px] text-white/70">
                  ▶ 60-second intro from Ian (no sales pitch).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== NEW: HOW IT WORKS ===== */}
        <section id="how-it-works" className="py-20 sm:py-24 px-5 scroll-mt-20 bg-gradient-to-b from-[#070b16] via-[#0a1226] to-[#070b16]">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">How it works</p>
              <h2 className="text-3xl sm:text-4xl font-bold leading-[1.1] tracking-tight mb-4">Three simple steps</h2>
              <p className="text-base text-white/80 max-w-xl mx-auto leading-relaxed">Start with a short, free enquiry. I’ll help you work out whether a course is right for you.</p>
            </div>

            <ol className="relative pl-14 space-y-7 before:absolute before:left-5 before:top-3 before:bottom-3 before:w-px before:bg-gradient-to-b before:from-teal-400/60 before:via-teal-400/20 before:to-transparent">
              {[
                { t: 'Check availability with Ian', d: 'Choose a course and send your details, or text me directly. There’s no payment or commitment to enquire.' },
                { t: 'Agree your course plan', d: 'We’ll discuss your experience, availability and test plans. Review the booking terms before deciding.' },
                { t: 'Prepare with consistent coaching', d: isCheltenham ? 'Learn with me personally, with agreed lesson locations and clear feedback on your progress.' : 'Learn with me personally, with practice around Chippenham or Kingswood and feedback on your progress.' },
              ].map((step, i) => (
                <li key={step.t} className="relative">
                  <span className="absolute -left-14 top-0 h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-[#06121a] font-bold flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(20,184,166,0.6)]">
                    {i + 1}
                  </span>
                  <h3 className="text-lg font-semibold text-white">{step.t}</h3>
                  <p className="mt-1 text-base text-white/80 leading-relaxed">{step.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>




        {/* ===== 4b. INTENSIVE COURSE PRICING (Manual / Automatic) ===== */}
        <IntensiveCoursePricing onEnquire={openEnquiry} enquiryForm={enquiry?.origin === 'card' ? enquiryForm : null} />

        {/* ===== 4c. PERSONAL COURSE VALUE ===== */}
        <section aria-labelledby="why-intensives-cost-more" className="py-20 sm:py-24 px-5 bg-gradient-to-b from-[#070b16] to-[#0a1124]">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">Personal tuition</p>
              <h2 id="why-intensives-cost-more" className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight mb-4">
                A course planned around you
              </h2>
              <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                Your course is more than a block of hours: personal instruction, thoughtful planning and practical preparation, tailored to your experience and availability.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-10">
              {[
                { title: 'Personal instruction throughout', body: 'I teach every session, so your feedback and course plan stay consistent.' },
                { title: 'A plan tailored to your driving', body: 'We focus on the skills you need to develop, rather than assuming every learner needs the same programme.' },
                { title: 'Planning around your availability', body: 'We discuss timing together and plan sessions around your availability, subject to my diary.' },
                { title: isCheltenham ? 'A meeting point that works' : 'Door-to-door pickup', body: isCheltenham ? 'Discuss your Cheltenham location with me; pickup and travel arrangements are agreed before booking.' : 'Bath-based pickup makes it easier to fit your course into your day.' },
                { title: 'Mock test and pre-drive warm-up', body: 'Included preparation helps you understand the test format and identify what still needs practice.' },
                { title: 'Structured around the 27 DVSA skills', body: 'Clear feedback helps you understand your progress and the skills to work on next.' },
              ].map((f) => (
                <div key={f.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal-300 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-white mb-1">{f.title}</h3>
                      <p className="text-[14.5px] text-white/75 leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-teal-300/25 bg-gradient-to-br from-teal-500/[0.08] to-cyan-500/[0.04] p-6 sm:p-8 text-center">
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                You can ask questions before committing. I’ll discuss the course options honestly and help you judge what fits your experience. Progress and test results vary; further practice may be needed.
              </p>
            </div>
          </div>
        </section>

        {/* ===== 4d. YOUR DRIVING JOURNEY TEASER ===== */}
        <section aria-labelledby="your-driving-journey-teaser" className="py-20 sm:py-24 px-5 bg-gradient-to-b from-[#0a1124] to-[#070b16]">
          <div className="container mx-auto max-w-4xl">
            <div className="rounded-3xl border border-teal-300/25 bg-gradient-to-br from-teal-500/[0.10] via-cyan-500/[0.05] to-transparent p-8 sm:p-12 text-center backdrop-blur">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">The learning pathway</p>
              <h2 id="your-driving-journey-teaser" className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight mb-5">
                <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-sky-300 bg-clip-text text-transparent">
                  Your Driving Journey
                </span>
              </h2>
              <div className="max-w-2xl mx-auto text-white/85 text-base sm:text-lg leading-relaxed space-y-3 mb-8">
                <p>Discover how every learner progresses through a carefully structured learning journey designed to build confidence step by step.</p>
                <p>This isn't simply a collection of driving lessons. It's a planned route from your very first lesson to becoming a safe, confident and independent driver.</p>
              </div>
              <Link
                to="/your-driving-journey"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl font-bold text-base text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-[0_10px_40px_-10px_rgba(45,212,191,0.7)] hover:brightness-110 active:scale-[0.98] transition-all"
              >
                Explore Your Driving Journey
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>








        {/* ===== 5. AREAS COVERED ===== */}
        <section id="areas" className="py-20 sm:py-24 px-5 scroll-mt-20 bg-gradient-to-b from-[#070b16] via-[#0a1226] to-[#070b16]">
          <div className="container mx-auto max-w-5xl">
            <div className="mb-10">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">
                <MapPin className="inline h-3 w-3 mr-1" /> Where Ian teaches
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold leading-[1.1] tracking-tight mb-5">{isCheltenham ? 'Planning your course from Cheltenham' : 'Areas covered'}</h2>
              <p className="text-white/85 text-base max-w-2xl leading-relaxed">
                {isCheltenham ? 'Looking for intensive tuition in Cheltenham? Tell me where you are and when you would like to learn. I’ll confirm the practical arrangements and my availability before you decide.' : 'Best-Drive is based in Bath, with door-to-door pickup across the city and surrounding villages.'}
              </p>
              <p className="text-white/85 text-base max-w-2xl leading-relaxed mt-3">
                {isCheltenham ? 'Best-Drive is based in Bath, not a Cheltenham branch. Test dates are booked through DVSA by you; this enquiry does not promise test availability or reserve a course.' : 'Test prep focuses on Chippenham and Kingswood — Bath has no test centre, so every learner is coached on the routes that actually matter on test day.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {pageAreas.map((a) => (
                <div key={a.name} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-7 w-7 rounded-full bg-teal-400/15 border border-teal-300/30 flex items-center justify-center">
                      <MapPin className="h-3.5 w-3.5 text-teal-300" />
                    </span>
                    <p className="text-lg font-semibold text-white">{a.name}</p>
                  </div>
                  <p className="text-[13.5px] text-white/75 leading-relaxed">{a.note}</p>
                </div>
              ))}
            </div>

            {/* Static map placeholder */}
            {!isCheltenham && <div
              role="img"
              aria-label="Map showing Bath, Chippenham and Kingswood pins"
              className="relative h-44 sm:h-56 rounded-2xl border border-white/10 overflow-hidden bg-[#0a1530]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 30% 60%, rgba(45,212,191,0.18), transparent 55%), radial-gradient(circle at 70% 35%, rgba(56,189,248,0.15), transparent 55%), linear-gradient(180deg, #0c1730 0%, #0a1124 100%)',
              }}
            >
              <div className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                  backgroundSize: '36px 36px',
                }} />
              {[
                { name: 'Bath', x: '30%', y: '60%' },
                { name: 'Chippenham', x: '65%', y: '35%' },
                { name: 'Kingswood', x: '15%', y: '40%' },
              ].map((pin) => (
                <div key={pin.name} className="absolute -translate-x-1/2 -translate-y-full" style={{ left: pin.x, top: pin.y }}>
                  <div className="flex flex-col items-center">
                    <span className="px-2 py-0.5 mb-1 rounded-full text-[10px] font-semibold bg-slate-950/80 border border-white/15 text-white/90">{pin.name}</span>
                    <MapPin className="h-6 w-6 text-emerald-400 drop-shadow-[0_4px_12px_rgba(20,184,166,0.6)]" fill="rgba(20,184,166,0.3)" />
                  </div>
                </div>
              ))}
            </div>}
          </div>
        </section>

        {/* ===== 6. PRICING FAQ ===== */}
        <section id="pricing-faq" className="py-20 sm:py-24 px-5 scroll-mt-20">
          <div className="container mx-auto max-w-2xl">
            <div className="text-center mb-8">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">
                <PoundSterling className="inline h-3 w-3 mr-1" /> Course pricing
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold leading-[1.1] tracking-tight mb-4">Simple, honest course pricing</h2>
              <p className="text-base text-white/80 leading-relaxed">
                Clear intensive course prices, mock test included, no hidden extras.
              </p>
            </div>

            <div className="space-y-3">
              {pricingFaqs.map((f) => (
                <details key={f.q} className="group rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-5 open:border-teal-300/30 transition-colors">
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="text-base font-semibold text-white leading-snug">{f.q}</h3>
                    <span className="flex-shrink-0 h-6 w-6 rounded-full border border-white/15 flex items-center justify-center text-white/70 group-open:rotate-45 transition-transform text-base leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-[14.5px] text-white/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>




        {/* ===== 7. SOCIAL PROOF — CAROUSEL ===== */}
        <section id="reviews" className="scroll-mt-20 py-20 sm:py-24 px-5 bg-[#0b1220]">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">Why learners choose BEST-DRIVE</p>
              <h2 className="text-3xl sm:text-4xl font-bold leading-[1.1] tracking-tight mb-4">Real students. Real passes.</h2>
              <p className="text-base text-white/80 max-w-xl mx-auto leading-relaxed">{isCheltenham ? 'Existing Best-Drive learner stories from Chippenham and Kingswood, shared unchanged — not Cheltenham reviews.' : 'No stock photos, no actors. Genuine pass photos and quotes from learners taught by Ian.'}</p>
            </div>

            {/* Stat row */}
            <p className="text-center text-[13.5px] sm:text-sm text-white/85 mb-6">
              <span className="inline-flex items-center gap-1">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> 5.0 Google rating
              </span>
              <span className="mx-2 text-white/30">·</span>
              <span>DVSA Grade A instructor</span>
              <span className="mx-2 text-white/30">·</span>
              <span>Every course taught personally by Ian</span>
            </p>

            {/* Carousel */}
            <div className="-mx-5 px-5 overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex gap-4 pb-2">
                {testimonials.map((t) => (
                  <figure
                    key={t.name}
                    className="snap-start shrink-0 w-[78%] sm:w-[44%] lg:w-[31%] rounded-2xl overflow-hidden bg-white/[0.04] border border-white/10 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img src={t.image} alt={`${t.name} passed driving test with Best-Drive`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-[#0b1220]/30 to-transparent" />
                      {t.firstTime && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase bg-emerald-400 text-[#06121a]">
                          Passed first time
                        </span>
                      )}
                    </div>
                    <figcaption className="p-5">
                      <p className="text-base text-white/90 italic mb-3 leading-snug">“{t.quote}”</p>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-[12.5px] text-white/65 mt-0.5">{t.centre} · {t.when}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            <div className="mt-6 text-center">
              <a
                href="https://g.page/r/best-drive-bath"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 text-sm font-semibold"
              >
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                Read all reviews on Google
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

        </section>

        {/* ===== 8. FAQs ===== */}
        <section id="faqs" className="py-20 sm:py-24 px-5 scroll-mt-20">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-teal-300 mb-4">FAQs</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight">Intensive driving courses in {locationName} — common questions</h2>
            </div>

            <div className="space-y-3">
              {pageFaqs.map((f, i) => (
                <details key={i} className="group rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-5 open:border-teal-300/30 transition-colors">
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="text-base font-semibold text-white leading-snug">{f.q}</h3>
                    <span className="flex-shrink-0 h-6 w-6 rounded-full border border-white/15 flex items-center justify-center text-white/70 group-open:rotate-45 transition-transform text-base leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-[14.5px] text-white/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 9. FINAL CTA ===== */}
        <section id="contact" className="relative py-20 sm:py-24 px-5 scroll-mt-20 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'radial-gradient(circle at 30% 30%, rgba(20,184,166,0.16), transparent 55%), radial-gradient(circle at 70% 70%, rgba(56,189,248,0.12), transparent 60%)',
            }}
          />
          <div className="container mx-auto max-w-2xl text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-5">
              Ready to start an intensive driving course in {locationName}?
            </h2>
            <p className="text-white/85 text-base sm:text-lg mb-9 leading-relaxed max-w-xl mx-auto">
              Check availability and discuss the right course size with me. Your enquiry is free, with no commitment or upfront payment to enquire.
            </p>

            <Button
              type="button"
              onClick={() => openEnquiry()}
              aria-controls="course-enquiry"
              aria-label="Check availability with Ian"
              className="btn-premium h-auto rounded-full text-base sm:text-lg px-7 py-4"
            >
              <MessageCircle className="h-5 w-5" />
              Check availability with Ian
            </Button>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/80 text-sm">
              <a href={PHONE_TEL} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">07564 936033</span>
              </a>
              <span className="hidden sm:inline text-white/30">·</span>
              <a href="mailto:help@best-drive.co.uk" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="h-4 w-4" />
                <span className="font-medium">help@best-drive.co.uk</span>
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 border-t border-white/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">Intensive driving courses near you</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/intensive-driving-courses-bath" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-all">
                <p className="font-semibold text-white mb-1">Intensive courses — Bath</p>
                <p className="text-[13px] text-white/70">{isCheltenham ? '5, 10, 15, 25 & 35-hour courses. Auto & manual.' : '5, 10, 15, 20, 30 & 40-hour courses. Auto & manual.'}</p>
              </Link>
              <Link to="/intensive-driving-courses-chippenham" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-all">
                <p className="font-semibold text-white mb-1">Chippenham test centre</p>
                <p className="text-[13px] text-white/70">Practice for rural roads and dual carriageways.</p>
              </Link>
              <Link to="/intensive-driving-courses-kingswood" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-all">
                <p className="font-semibold text-white mb-1">Kingswood test centre</p>
                <p className="text-[13px] text-white/70">Bristol-side preparation for varied roads and roundabouts.</p>
              </Link>
              <Link to="/before-your-test" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-all">
                <p className="font-semibold text-white mb-1">Before your test</p>
                <p className="text-[13px] text-white/70">Pass rates, examiner routes and what to expect.</p>
              </Link>
            </div>
          </div>
        </section>
        </main>

        <SEOFooter showCookieSettings={isCheltenham} />
      </div>
    </>
  );
};

export default BestDriveBath;
